import Card from "@/components/common/Card";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { Switch } from "@headlessui/react";
import { motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { LANGUAGES, ACCESSIBILITY_OPTIONS } from "@/constants/settings";

interface AppearanceSettingsProps {
  user: any;
  onLanguageChange: (language: "en" | "am" | "ti") => Promise<void>;
  isLoading: boolean;
}

const AppearanceSettings = ({ user, onLanguageChange, isLoading }: AppearanceSettingsProps) => {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useAuth();

  // Accessibility settings
  const [accessibilitySettings, setAccessibilitySettings] = useState({
    highContrast: false,
    reducedMotion: false,
    largerText: false,
  });

  const handleThemeChange = async (newTheme: "light" | "dark") => {
    try {
      const updatedUser = {
        ...user,
        preferences: {
          ...user?.preferences,
          theme: newTheme,
        },
      };
      await updateUser(updatedUser);
      setTheme(newTheme);
    } catch (error) {
      console.error("Error updating theme:", error);
    }
  };

  const handleAccessibilityToggle = (
    setting: keyof typeof accessibilitySettings
  ) => {
    setAccessibilitySettings((prev) => ({
      ...prev,
      [setting]: !prev[setting],
    }));
    toast.success(t("settings.accessibilityUpdated"));
  };

  const languages = LANGUAGES.map(lang => ({
    ...lang,
    name: t(lang.name)
  }));

  const accessibilityOptions = ACCESSIBILITY_OPTIONS.map(option => ({
    ...option,
    label: t(option.label),
    description: t(option.description)
  }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="mb-6 text-xl font-semibold text-gray-900 dark:text-white">
        {t("settings.appearance")}
      </h2>

      <div className="space-y-6">
        {/* Language Settings */}
        <Card className="p-6">
          <h3 className="mb-4 text-base font-medium text-gray-900 dark:text-white">
            {t("settings.language")}
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => onLanguageChange(language.code as "en" | "am" | "ti")}
                disabled={isLoading}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  i18n.language === language.code
                    ? "border-primary-500 bg-primary-50 text-primary-700 dark:border-primary-400 dark:bg-primary-900/20 dark:text-primary-300"
                    : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-700"
                } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <div className="flex items-center">
                  <div className="flex h-4 w-4 items-center justify-center">
                    {i18n.language === language.code && (
                      <div className="h-2 w-2 rounded-full bg-primary-600" />
                    )}
                  </div>
                  <span className="ml-3 text-sm font-medium">
                    {language.name}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Theme Settings */}
        <Card className="p-6">
          <h3 className="mb-4 text-base font-medium text-gray-900 dark:text-white">
            {t("settings.theme")}
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              onClick={() => handleThemeChange("light")}
              className={`rounded-lg border p-4 text-left transition-colors ${
                theme === "light"
                  ? "border-primary-500 bg-primary-50 text-primary-700 dark:border-primary-400 dark:bg-primary-900/20 dark:text-primary-300"
                  : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-700"
              }`}
            >
              <div className="flex items-center">
                <div className="flex h-4 w-4 items-center justify-center">
                  {theme === "light" && (
                    <div className="h-2 w-2 rounded-full bg-primary-600" />
                  )}
                </div>
                <span className="ml-3 text-sm font-medium">
                  {t("settings.lightTheme")}
                </span>
              </div>
            </button>
            <button
              onClick={() => handleThemeChange("dark")}
              className={`rounded-lg border p-4 text-left transition-colors ${
                theme === "dark"
                  ? "border-primary-500 bg-primary-50 text-primary-700 dark:border-primary-400 dark:bg-primary-900/20 dark:text-primary-300"
                  : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-700"
              }`}
            >
              <div className="flex items-center">
                <div className="flex h-4 w-4 items-center justify-center">
                  {theme === "dark" && (
                    <div className="h-2 w-2 rounded-full bg-primary-600" />
                  )}
                </div>
                <span className="ml-3 text-sm font-medium">
                  {t("settings.darkTheme")}
                </span>
              </div>
            </button>
          </div>
        </Card>

        {/* Accessibility Settings */}
        <Card className="p-6">
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
            {t("settings.accessibility")}
          </h3>
          <div className="space-y-4">
            {accessibilityOptions.map((option) => (
              <div key={option.key} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {option.label}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {option.description}
                  </p>
                </div>
                <Switch
                  checked={accessibilitySettings[option.key]}
                  onChange={() => handleAccessibilityToggle(option.key)}
                  className={`${
                    accessibilitySettings[option.key]
                      ? "bg-primary-600"
                      : "bg-gray-200 dark:bg-gray-700"
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      accessibilitySettings[option.key]
                        ? "translate-x-6"
                        : "translate-x-1"
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </motion.div>
  );
};

export default AppearanceSettings;
